import React, { useContext, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as Duplicate } from '../../../../../assets/icons/duplicate.svg';
import { ReactComponent as Details } from '../../../../../assets/icons/open-eye-ne.svg';
import { ReactComponent as TrashNE } from '../../../../../assets/icons/trash-ne.svg';
import CustomTooltipNE from '../../../../../shared/components/CustomTooltipNE/CustomTooltipNE';
import StyledCircularProgress from '../../../../../shared/components/StyledCircularProgress/StyledCircularProgress';
import ToastNE from '../../../../../shared/components/ToastNE/ToastNE';
import { NEW_STORY_ROUTE } from '../../../../../shared/constants/auth';
import { HIDE_STORY_MODAL, STORY_DETAIL_ERROR_MODAL } from '../../../../../shared/constants/modal';
import {
  CANCEL_DELETE_STORY,
  CLICK_ACTION,
  DELETE_STORY,
  DUPLICATE_STORY,
  HIDE_STORY,
  SEE_DETAILS_STORY
} from '../../../../../shared/constants/tagManager';
import {
  DEFAULT_TOAST_DATA,
  HIDE_STORY_ERROR_TOAST,
  HIDE_STORY_SUCCESS_TOAST
} from '../../../../../shared/constants/toast';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import { ModalContext } from '../../../../../shared/context/modal/modalContext';
import { StoriesContext } from '../../../../../shared/context/stories/storiesContext';
import { IStoryListData } from '../../../../../shared/interfaces/stories';
import { IToast } from '../../../../../shared/interfaces/toast';
import Stories from '../../../../../shared/services/api/communication/stories';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_STORIES_LIST } from '../../../../../shared/translations/stories';
import StoryDetailModal from '../StoryDetailModal/StoryDetailModal';

import './StoryListActions.scss';

interface IStoryListActionsProps {
  storyData: IStoryListData;
}

const StoryListActions = ({ storyData }: IStoryListActionsProps) => {
  const [storyDetailModalOpen, setStoryDetailModalOpen] = useState(false);
  const [isDuplicatingStory, setIsDuplicatingStory] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastData, setToastData] = useState<IToast>(DEFAULT_TOAST_DATA);

  const { company } = useContext(CompanyContext);
  const { changeStoryDuplication } = useContext(StoriesContext);
  const { handleOpenModal, handleCloseModal } = useContext(ModalContext);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const getStoryDetail = async () => {
    try {
      const storyDetail = await Stories.getStoryDetail(company.id, storyData.id);
      changeStoryDuplication(storyDetail);
      navigate(NEW_STORY_ROUTE);
      TagManagerService.pushEvent(CLICK_ACTION, DUPLICATE_STORY, { id: storyData.id });
    } catch {
      handleOpenModal(STORY_DETAIL_ERROR_MODAL);
      setIsDuplicatingStory(false);
    }
  };

  const deleteStory = async () => {
    try {
      handleCloseModal();
      await Stories.deleteStory(storyData.id);
      setToastData(HIDE_STORY_SUCCESS_TOAST);
      setShowToast(true);
      storyData.status = 'hidden';
      TagManagerService.pushEvent(CLICK_ACTION, DELETE_STORY, { id: storyData.id, type: storyData.mediaType });
    } catch {
      setToastData(HIDE_STORY_ERROR_TOAST);
      setShowToast(true);
    }
  };

  const closeModal = () => {
    TagManagerService.pushEvent(CLICK_ACTION, CANCEL_DELETE_STORY, { id: storyData.id });
    handleCloseModal();
  };

  const handleDuplicate = async () => {
    setIsDuplicatingStory(true);
    await getStoryDetail();
  };

  const handleHide = () => {
    if (HIDE_STORY_MODAL.primaryButton) {
      HIDE_STORY_MODAL.primaryButton.action = deleteStory;
    }
    if (HIDE_STORY_MODAL.secondaryButton) {
      HIDE_STORY_MODAL.secondaryButton.action = closeModal;
    }
    handleOpenModal(HIDE_STORY_MODAL);
    TagManagerService.pushEvent(CLICK_ACTION, HIDE_STORY, { id: storyData.id });
  };

  const handleDetails = () => {
    setStoryDetailModalOpen(true);
    TagManagerService.pushEvent(CLICK_ACTION, SEE_DETAILS_STORY, { id: storyData.id });
  };

  const handleCloseToast = (event: React.SyntheticEvent | React.MouseEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowToast(false);
  };

  const handleCloseDetailsModal = () => {
    setStoryDetailModalOpen(false);
  };

  return (
    <>
      {storyData.id && (
        <div className="story-list-action">
          <CustomTooltipNE
            placement="top"
            tooltipTitle={<div className="tooltip-text"> {t(TRANSLATION_STORIES_LIST.table.tooltip.details)} </div>}
          >
            <Details
              className="story-list-action-icon cursor-pointer"
              data-testid="details-icon"
              onClick={handleDetails}
            />
          </CustomTooltipNE>

          {isDuplicatingStory ? (
            <div className="story-list-action-icon">
              <StyledCircularProgress />
            </div>
          ) : (
            <CustomTooltipNE
              placement="top"
              tooltipTitle={<div className="tooltip-text"> {t(TRANSLATION_STORIES_LIST.table.tooltip.duplicate)} </div>}
            >
              <Duplicate
                className="story-list-action-icon cursor-pointer"
                data-testid="duplicate-icon"
                onClick={handleDuplicate}
              />
            </CustomTooltipNE>
          )}

          {storyData.status !== 'hidden' && (
            <CustomTooltipNE
              placement="top"
              tooltipTitle={<div className="tooltip-text"> {t(TRANSLATION_STORIES_LIST.table.tooltip.hide)} </div>}
            >
              <TrashNE
                className="story-list-action-icon cursor-pointer"
                data-testid="trash-icon"
                onClick={handleHide}
              />
            </CustomTooltipNE>
          )}
        </div>
      )}

      {storyDetailModalOpen && (
        <StoryDetailModal
          isModalOpen={storyDetailModalOpen}
          handleCloseDetailModal={handleCloseDetailsModal}
          storyId={storyData.id}
        />
      )}

      <ToastNE
        title={t(toastData.title)}
        description={t(toastData.description)}
        icon={toastData.icon}
        open={showToast}
        button={toastData.button}
        buttonText={t(toastData.buttonText)}
        buttonAction={handleCloseToast}
        handleClose={handleCloseToast}
      />
    </>
  );
};

export default StoryListActions;
